const setInputFiles = (dataTransfer, input) => {
  if (!dataTransfer) {
    return;
  }
  input.onchange = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  input.files = dataTransfer.files;
  input.onchange = null;
};
const isDataTransferSupported = () => {
  try {
    new DataTransfer();
    return true;
  } catch (e) {
    return false;
  }
};
const setDataTransfer = (files, options = {}, fileNameToExclude) => {
  const dataTransfer = isDataTransferSupported() ? new DataTransfer() : null;
  const selectedFiles = Array.from(files);
  let fileNames = [];
  let errors = null;
  if (options.maxFiles && selectedFiles.length > options.maxFiles) {
    if (!errors) {
      errors = {};
      errors.maxFilesExceeded = selectedFiles.slice(options.maxFiles).map((file) => file.name);
    }
    selectedFiles.splice(options.maxFiles);
  }
  selectedFiles.forEach((file) => {
    if (options.maxFileSize && file.size > options.maxFileSize) {
      if (!errors) {
        errors = {};
      }
      if (!errors.maxFileSizeExceeded) {
        errors.maxFileSizeExceeded = [file.name];
        return;
      }
      errors.maxFileSizeExceeded.push(file.name);
      return;
    }
    if (file.name !== fileNameToExclude) {
      dataTransfer && dataTransfer.items.add(file);
      dataTransfer && fileNames.push(file.name);
    }
  });
  if (!dataTransfer) {
    fileNames = Array.from(files).map((f) => f.name);
  }
  return {
    dataTransfer,
    errors,
    fileNames
  };
};
const renderDefaultError = () => `An error occurred attaching a file`;
const clearContents = (el) => {
  [...el.children].forEach((node) => {
    el.removeChild(node);
  });
  return el;
};
const renderIconSvg = (icon, options, path = "") => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  svg.classList.add(...["tds-icon", String(options.small || "") && "tds-icon--small", icon]);
  svg.setAttribute("aria-hidden", "true");
  use.setAttribute("href", `${path}#${icon}`);
  svg.appendChild(use);
  return svg;
};
const dispatchCustomEvent = (element, eventName, detail) => {
  const customEvent = new CustomEvent(eventName, {
    bubbles: true,
    cancelable: true,
    composed: true,
    detail
  });
  element.dispatchEvent(customEvent);
};
const handleErrorMessage = (fileInput, errors, defaultError) => {
  if (errors?.maxFileSizeExceeded) {
    return fileInput.dataset.onMaxFileSizeExceeded || defaultError;
  }
  if (errors?.maxFilesExceeded) {
    return fileInput.dataset.onMaxFilesExceeded || defaultError;
  }
  return defaultError;
};
const renderFileErrors = (fileInput, errors, iconRenderer) => {
  const formItem = fileInput.closest(".tds-form-item");
  const formInput = fileInput.closest(".tds-form-input");
  if (!formItem)
    return;
  let formFeedbackWrap = formItem.querySelector(".tds-form-feedback");
  if (!formFeedbackWrap) {
    formFeedbackWrap = document.createElement("div");
    formFeedbackWrap.classList.add("tds-form-feedback", "tds-form-feedback-file");
    formItem.insertBefore(formFeedbackWrap, formInput);
  }
  let formFeedback = formItem.querySelector(".tds-form-feedback-text");
  if (!formFeedback) {
    formFeedback = document.createElement("div");
    formFeedback.classList.add("tds-form-feedback-text");
    formFeedbackWrap.appendChild(formFeedback);
  }
  clearContents(formFeedback);
  if (!errors) {
    formItem.classList.remove("tds-form--error");
    if (formFeedbackWrap)
      formItem.removeChild(formFeedbackWrap);
    return;
  }
  formItem.classList.add("tds-form--error");
  const fragment = document.createDocumentFragment();
  const statusIcon = iconRenderer("tds-icon-error-small-filled", { small: true });
  const errorText = document.createElement("span");
  errorText.textContent = handleErrorMessage(fileInput, errors, renderDefaultError());
  fragment.appendChild(statusIcon);
  fragment.appendChild(errorText);
  formFeedback.appendChild(fragment);
};
const fileListItem = (fileInput, name, iconRenderer) => {
  const li = document.createElement("li");
  li.classList.add("tds-list-item--file");
  const fileNameText = document.createElement("span");
  fileNameText.textContent = name;
  li.appendChild(fileNameText);
  if (fileInput.dataset.dtSupported) {
    const closeIcon = iconRenderer("tds-icon-close-small", { small: true });
    const removeFileButton = document.createElement("button");
    removeFileButton.appendChild(closeIcon);
    removeFileButton.addEventListener("click", () => {
      dispatchCustomEvent(fileInput, "file-item-removed", { name });
      setFileList(fileInput, Array.from(fileInput.files || []), name, iconRenderer);
    });
    li.appendChild(removeFileButton);
  }
  return li;
};
const renderFileList = (fileInput, fileNames, iconRenderer) => {
  const formItem = fileInput.closest(".tds-form-item");
  const formInput = fileInput.closest(".tds-form-input");
  if (!formItem)
    return;
  let formLabelFiles = formItem.querySelector(".tds-form-label-files");
  if (!formLabelFiles) {
    formLabelFiles = document.createElement("div");
    formLabelFiles.classList.add("tds-form-label");
    formLabelFiles.classList.add("tds-form-label-files");
    formItem.insertBefore(formLabelFiles, formInput);
  }
  let filesList = formItem.querySelector(".tds-form-label-files > .tds-list");
  if (!filesList) {
    filesList = document.createElement("ul");
    filesList.classList.add("tds-list");
    formLabelFiles.appendChild(filesList);
  }
  clearContents(filesList);
  const itemsFragment = fileNames.reduce((fragment, name) => {
    fragment.appendChild(fileListItem(fileInput, name, iconRenderer));
    return fragment;
  }, document.createDocumentFragment());
  filesList.appendChild(itemsFragment);
};
const setFileList = (fileInput, files, fileNameToExclude, iconRenderer) => {
  const maxFileSize = parseInt(fileInput.dataset.maxFileSize || "") || void 0;
  const maxFiles = parseInt(fileInput.dataset.maxFiles || "") || void 0;
  const { dataTransfer, errors, fileNames } = setDataTransfer(
    files,
    { maxFileSize, maxFiles },
    fileNameToExclude
  );
  setInputFiles(dataTransfer, fileInput);
  renderFileList(fileInput, fileNames, iconRenderer);
  renderFileErrors(fileInput, errors, iconRenderer);
  if (dataTransfer) {
    dispatchCustomEvent(fileInput, "file-list-changed", {
      errors,
      files: Array.from(dataTransfer.files)
    });
  }
};
const initFileUploadInput = (input, props) => {
  if (!input) {
    console.warn("TDS Form Input malformed, requires an input element within each .tds-form-input");
    return;
  }
  if (isDataTransferSupported()) {
    input.setAttribute("data-dt-supported", "true");
  }
  let files = [];
  const iconRenderer = (icon, options) => renderIconSvg(icon, options, props.iconPath);
  const onInputChange = (e) => {
    const newFiles = Array.from(e.target?.files || []);
    if (!e.target?.hasAttribute("multiple")) {
      setFileList(e.target, newFiles, null, iconRenderer);
      return;
    }
    const updatedFiles = [...files, ...newFiles].filter(
      (v, i, a) => a.findIndex((t) => t.name === v.name) === i
    );
    setFileList(e.target, updatedFiles, null, iconRenderer);
  };
  const onFileItemRemoved = (e) => {
    const index = files.findIndex((f) => f.name === e.detail.name);
    files.splice(index, 1);
  };
  const onFileListChanged = (e) => {
    files = Array.from(e.detail.files);
  };
  input.addEventListener("change", onInputChange);
  input.addEventListener("file-list-changed", onFileListChanged);
  input.addEventListener("file-item-removed", onFileItemRemoved);
};
const initFileUploadInputs = (props = {}) => {
  const { parent = document } = props;
  const inputs = Array.from(
    parent.querySelectorAll(".tds-form-input-file-upload")
  );
  inputs.forEach((input) => {
    initFileUploadInput(input, props);
  });
};
export {
  clearContents,
  dispatchCustomEvent,
  fileListItem,
  handleErrorMessage,
  initFileUploadInput,
  initFileUploadInputs,
  isDataTransferSupported,
  renderDefaultError,
  renderFileErrors,
  renderFileList,
  renderIconSvg,
  setDataTransfer,
  setFileList,
  setInputFiles
};
